import { computed } from 'vue';
import type { Ref } from 'vue';
import type { RouteLocationNormalizedLoadedGeneric } from 'vue-router';
import { navActiveStatesUpdate, type NavMenuSection } from '../../../cross-app-frontend/navigation';
import { facilityFromRouteOrLastVisited, yearFromRouteOrLastVisited } from '@/router';
import { slugFromName } from '../../../tri-shared/services/formatting';
import { facilityPermissions } from '@/services/navigation/navigation-permissions';

export function useTierIINavMenu(currentRoute: Ref<RouteLocationNormalizedLoadedGeneric>): Ref<NavMenuSection[]> {
  return computed((): NavMenuSection[] => {
    const facility = facilityFromRouteOrLastVisited.value;
    if (!facility) return [];

    const facilityKey = facility.facilityID as unknown as number;
    const perms = facilityPermissions.value[facilityKey];
    const hasAdministerFacility = perms?.hasAdministerFacility ?? false;
    const hasManageUsers = perms?.hasManageUsers ?? false;
    const isAdmin = hasAdministerFacility;
    const isSuperAdmin = perms?.isSuperAdmin ?? false;

    const facilityParams = {
      facilityID: facility.facilityID,
      facilitySlug: slugFromName(facility.name),
    };

    const facilityParamsWithYear = {
      ...facilityParams,
      year: yearFromRouteOrLastVisited.value,
    };

    const result: NavMenuSection[] = [
      {
        id: 'workflow',
        title: 'Tier II',
        groups: [
          {
            title: 'Reporting',
            children: [
              {
                title: 'Overview',
                icon: 'fa-building',
                type: 'link',
                isActive: false,
                to: { name: 'TierII Overview', params: facilityParams },
              },
            ],
          },
          {
            title: 'Inventory',
            children: [
              {
                title: 'Products',
                icon: 'fa-boxes-stacked',
                type: 'link',
                isActive: false,
                to: { name: 'TierII Facility Products', params: facilityParamsWithYear },
              },
              {
                title: 'Questionnaire',
                icon: 'fa-pen-to-square',
                type: 'link',
                isActive: false,
                to: { name: 'TierII Inventory Questionnaire', params: facilityParamsWithYear },
              },
              {
                title: 'Threshold Review',
                icon: 'fa-chart-bar',
                type: 'link',
                isActive: false,
                to: { name: 'TierII Inventory Threshold', params: facilityParamsWithYear },
              },
              {
                title: 'Report Form',
                icon: 'fa-file-lines',
                type: 'link',
                isActive: false,
                to: { name: 'TierII Report Form', params: facilityParamsWithYear },
              },
              {
                title: 'Site Map',
                icon: 'fa-map',
                type: 'link',
                isActive: false,
                to: { name: 'TierII Site Map', params: facilityParamsWithYear },
              },
              {
                title: 'Submit Report',
                icon: 'fa-paper-plane',
                type: 'link',
                isActive: false,
                to: { name: 'TierII Export', params: facilityParamsWithYear },
              },
            ],
          },
        ],
      },
      ...(isAdmin || hasManageUsers || isSuperAdmin
        ? [
            {
              id: 'admin',
              title: 'Admin',
              groups: [
                {
                  title: 'Admin',
                  children: [
                    {
                      title: 'Submission Status',
                      icon: 'fa-list-check',
                      type: 'link' as const,
                      isActive: false,
                      to: { name: 'TierII Status' as const, params: facilityParamsWithYear },
                    },
                    {
                      title: 'Inventory Profiles',
                      icon: 'fa-clouds',
                      type: 'link' as const,
                      isActive: false,
                      to: { name: 'Inventory Profiles' as const, params: facilityParamsWithYear },
                    },
                    {
                      title: 'Chemicals',
                      icon: 'fa-flask-vial',
                      type: 'link' as const,
                      isActive: false,
                      to: { name: 'Chemicals' as const, params: facilityParamsWithYear },
                    },
                    {
                      title: 'Facilities',
                      icon: 'fa-city',
                      type: 'link' as const,
                      isActive: false,
                      to: { name: 'Facilities' as const, params: facilityParamsWithYear },
                    },
                    {
                      title: 'Users',
                      icon: 'fa-users',
                      type: 'link' as const,
                      isActive: false,
                      to: { name: 'Users' as const, params: facilityParamsWithYear },
                    },
                    ...(isSuperAdmin
                      ? [
                          {
                            title: 'State Requirements',
                            icon: 'fa-landmark',
                            type: 'link' as const,
                            isActive: false,
                            to: { name: 'TierII Admin Jurisdictions' as const, params: facilityParamsWithYear },
                          },
                          {
                            title: 'Chemical Thresholds',
                            icon: 'fa-scale-balanced',
                            type: 'link' as const,
                            isActive: false,
                            to: { name: 'TierII Admin Thresholds' as const, params: facilityParamsWithYear },
                          },
                        ]
                      : []),
                  ],
                },
              ],
            } as NavMenuSection,
          ]
        : []),
    ];

    navActiveStatesUpdate(currentRoute.value, result);
    return result;
  });
}
